import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { LoadingButton } from '@mui/lab';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Notification } from '@features/notifications';
import NotificationIcon from '@features/notifications/components/NotificationIcon/NotificationIcon';
import {
  formNotificationSecondaryText,
  formNotificationTitle,
} from '@features/notifications/components/NotificationListItem/utils';
import { useCreateApplicationNote } from '@features/notifications/hooks/useReadNotification';
import { authContext } from '@staffedupusa/shared-components';
import { Application } from '@features/applications';
import WotcSurvey from '@features/WotcSurvey/WotcSurvey';
import { notificationType } from '@features/notifications/NotificationsEnum';

type NotificationListItemProps = {
  notification: Notification;
  setErrorText: Dispatch<SetStateAction<string>>;
  handleClose: () => void;
  handleNotificationClick?: (application: Application) => void;
  setCongratulationsModal?: Dispatch<SetStateAction<boolean>>;
  handlePendingJobOfferNotificationClick?: (application: Application) => void;
  hanldeOpenThreads?: (application: Application) => void;
  setNotifications: Dispatch<SetStateAction<Notification[]>>
};

const NotificationListItem: FC<NotificationListItemProps> = ({
  notification,
  setErrorText,
  handleClose,
  setCongratulationsModal,
  handleNotificationClick,
  handlePendingJobOfferNotificationClick,
  hanldeOpenThreads,
  setNotifications
}) => {
  const { authData } = useContext(authContext);
  const [showWotcSurveyModal, setShowWotcSurveyModal] = useState(false);
  const isApplicant =
    authData?.user &&
    !authData?.user?.company &&
    !authData?.user.enterprise &&
    !authData?.user.is_admin &&
    !authData?.user.is_rockerbox;
  const navigate = useNavigate();

  const { readNotification, isReadNotificationLoading } =
    useCreateApplicationNote({ setErrorText });

  const handleView = () => {
    if (notification.notification_type !== 'WOTC SURVEY') {
      setNotifications((prevNotifications) =>
        prevNotifications.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n,
        ),
      );
      readNotification({
        notification_id: `${notification.id}`,
        ...(!isApplicant ? { user_id: notification.user_id } : {}),
      });
      handleClose();
    }
    switch (notification.notification_type) {
      case notificationType.NEW_MESSAGE:
        return navigate(
          isApplicant
            ? '/applicants/inbox/' + `?user=${notification.associated_user?.id}`
            : '/inbox' + `?user=${notification.associated_user?.id}`,
        );
      case notificationType.APPLICANT_COMPLETED_ONBOARDING:
      case notificationType.ONBOARDING_DOCUMENT_EXPIRED:
        return navigate('/dashboard/', {
          state: {
            application: JSON.stringify(notification.application!),
            tab: 'onboarding',
          },
        });
      case notificationType.EIN:
        return navigate('/employer/account#Company-Profile');
      case notificationType.ONBOARDING_DOCS_WAITING:
      case notificationType.ONBOARDING_DOCS_RESEND:
      case notificationType.POST_HIRING_DOCS:
        return navigate(`/applicants/onboarding/`);
      case notificationType.PENDING_INTERVIEW_INVITE:
      case notificationType.NO_CALENDAR_BLOCK:
        return isApplicant &&
          notification?.application &&
          handleNotificationClick
          ? handleNotificationClick(notification?.application)
          : authData?.user?.company
            ? navigate('/employer/calendar/')
            : authData?.user?.enterprise
              ? navigate('/enterprise/calendar/')
              : null;
      case notificationType.PENDING_JOB_OFFER:
        return (
          notification?.application &&
          handlePendingJobOfferNotificationClick &&
          handlePendingJobOfferNotificationClick(notification?.application)
        );
      case notificationType.WOTC_SURVEY:
        return setShowWotcSurveyModal(true);
      case notificationType.AUTO_ACTION_PERFORMED:
        return isApplicant && hanldeOpenThreads
          ? notification?.application &&
              hanldeOpenThreads(notification.application)
          : !isApplicant &&
              navigate('/dashboard/', {
                state: {
                  application: JSON.stringify(notification.application!),
                },
              });
    }
  };

  return (
    <ListItem
      sx={{ py: 1, flexDirection: { xs: 'column', sm: 'row' } }}
      style={{ justifyContent: 'space-between' }}
    >
      <Stack
        spacing={1}
        direction="row"
        justifyContent="center"
        flexGrow={1}
        sx={{ maxWidth: '410px' }}
      >
        <ListItemIcon sx={{ minWidth: '30px' }} style={{ marginTop: '3px' }}>
          <NotificationIcon notification={notification} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              component="div"
              dangerouslySetInnerHTML={{
                __html: formNotificationTitle(notification),
              }}
              lineHeight={1.2}
            />
          }
          secondary={formNotificationSecondaryText(notification)}
          style={{ color: 'black' }}
        />
      </Stack>
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent={{ xs: 'center', sm: 'end' }}
        alignItems="center"
        minWidth="250px"
        flexGrow={1}
        pt={{ xs: 2 }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<RemoveRedEyeOutlinedIcon />}
          onClick={handleView}
        >
          View
        </Button>
        <LoadingButton
          variant="outlined"
          size="small"
          startIcon={<CheckOutlinedIcon />}
          loading={isReadNotificationLoading}
          loadingPosition="start"
          onClick={() =>{
            setNotifications((prevNotifications) =>
              prevNotifications.map((n) =>
                n.id === notification.id ? { ...n, is_read: true } : n,
              ),
            );
            readNotification({
              notification_id: `${notification.id}`,
              ...(!isApplicant ? { user_id: notification.user_id } : {}),
            })
          }
        }
        >
          Got it
        </LoadingButton>
      </Stack>
      {showWotcSurveyModal ? (
        <WotcSurvey
          showWotcSurveyModal={showWotcSurveyModal}
          handleClose={() => setShowWotcSurveyModal(false)}
          setCongratulationsModal={setCongratulationsModal}
          notificationId={notification.id}
        />
      ) : null}
    </ListItem>
  );
};

export default NotificationListItem;
